import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getUserId } from "../security";
import { queueConfig } from "./offlineQueueConfig";

/* =====================================================
   GLOBAL CONFIG
   ===================================================== */
const defaultSerialize = (value) => JSON.stringify(value);
const defaultDeserialize = (value) => JSON.parse(value);
const OFFLINE_QUEUE_REGISTRY_EVENT = "shared:offline-queue-registry";
const OFFLINE_QUEUE_HISTORY_PREFIX = "seims.offline.sync-history.";
const offlineQueueRegistry = new Map();

const OFFLINE_QUEUE_PREFIX= "seims.offline."
const OFFLINE_QUEUE_EVENT = "offline-queue-updated";
let isGlobalSyncInProgress = false;

/* =====================================================
   STORAGE
   ===================================================== */
const estimateBytes = (rawValue) => {
  if (!rawValue) {
    return 0;
  }

  return rawValue.length * 2;
};

const readQueue = (storageKey, deserialize) => {
  let queue;
  try {
    const raw = localStorage.getItem(storageKey);
    queue = raw ? deserialize(raw) : [];
    if (!Array.isArray(queue)) queue = [];
  } catch {
    queue = [];
  }

  return queue;
};

const writeQueue = (storageKey, queue, serialize) => {
  localStorage.setItem(storageKey, serialize(queue));

  // ensure same-tab + cross-tab updates
  window.dispatchEvent(new Event(OFFLINE_QUEUE_EVENT));
};

const getSyncHistoryKey = (storageKey) => `${OFFLINE_QUEUE_HISTORY_PREFIX}${storageKey}`;

const readSyncHistory = (storageKey, deserialize = defaultDeserialize) => {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const raw = localStorage.getItem(getSyncHistoryKey(storageKey));
    const history = raw ? deserialize(raw) : [];
    return Array.isArray(history) ? history : [];
  } catch {
    return [];
  }
};

export const getOfflineQueueSyncHistory = (storageKey) => readSyncHistory(storageKey);

const snapshotSyncedItem = (item, response) => ({
  id: item?.id,
  createdAt: item?.createdAt || null,
  payload: item?.payload ?? null,
  meta: item?.meta ?? {},
  response: response
    ? {
        status: response?.status ?? null,
        data: response?.data ?? null,
      }
    : null,
});

const persistSyncHistory = (storageKey, summary) => {
  if (typeof window === "undefined" || !summary) {
    return;
  }

  try {
    const existingHistory = readSyncHistory(storageKey);
    const historyEntry = {
      timestamp: summary.timestamp,
      status: summary.status,
      total: summary.total,
      syncedCount: summary.syncedCount,
      failedCount: summary.failedCount,
      hasAuthError: summary.hasAuthError,
      syncedItems: (summary.synced || []).map(({ item, response }) => snapshotSyncedItem(item, response)),
      failedAttempts: summary.failedAttempts || [],
    };

    const nextHistory = [historyEntry, ...existingHistory].slice(0, 25);
    localStorage.setItem(getSyncHistoryKey(storageKey), JSON.stringify(nextHistory));
  } catch (error) {
    console.error(`[OfflineQueue] Failed to persist sync history for ${storageKey}:`, error);
  }
};

/* =====================================================
   TELEMETRY
   ===================================================== */
export const getOfflineQueueTelemetrySnapshot = () => {
  const entries = Array.from(offlineQueueRegistry.values());

  return {
    pendingCount: entries.reduce((acc, entry) => acc + Number(entry.queueCount || 0), 0),
    estimatedSizeBytes: entries.reduce((acc, entry) => acc + Number(entry.queueSizeBytes || 0), 0),
    queueCountByKey: Object.fromEntries(
      entries.map((entry) => [entry.storageKey, Number(entry.queueCount || 0)]),
    ),
    queueSizeBytesByKey: Object.fromEntries(
      entries.map((entry) => [entry.storageKey, Number(entry.queueSizeBytes || 0)]),
    ),
  };
};

export const publishRegistryState = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(OFFLINE_QUEUE_REGISTRY_EVENT, {
      detail: getOfflineQueueRegistryState(),
    })
  );
};

// Development: expose registry for testing/mocking
export const __DEV_getOfflineQueueRegistry = () => offlineQueueRegistry;

export const getOfflineQueueRegistryState = () => {
  const entries = Array.from(offlineQueueRegistry.values());
  const queueCount = entries.reduce((acc, entry) => acc + Number(entry.queueCount || 0), 0);
  const isSyncing = entries.some((entry) => entry.isSyncing);
  const queueSizeBytes = entries.reduce((acc, entry) => acc + Number(entry.queueSizeBytes || 0), 0);
  const attendeeCount = getTotalAttendeeCount();

  return {
    entries,
    queueCount,
    queueSizeBytes,
    isSyncing,
    queueCountByKey: Object.fromEntries(entries.map((entry) => [entry.storageKey, entry.queueCount || 0])),
    queueSizeBytesByKey: Object.fromEntries(entries.map((entry) => [entry.storageKey, entry.queueSizeBytes || 0])),
    attendeeCount,
  };
};

/* =====================================================
   QUEUE DISCOVERY
   ===================================================== */
const getQueueConfig = (key) => queueConfig?.[key] || {};

export const getOfflineQueueKeys = () => {
  const keys = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);

    if (key?.startsWith(OFFLINE_QUEUE_PREFIX)) {
      keys.push(key);
    }
  }

  return keys;
};

/* =====================================================
   GLOBAL ATTENDEE COUNT
   ===================================================== */
export const getTotalAttendeeCount = () => {
  const keys = getOfflineQueueKeys();

  return keys.reduce((total, key) => {
    try {
      const raw = localStorage.getItem(key);
      const queue = raw ? JSON.parse(raw) : [];

      if (!Array.isArray(queue)) return total;

      return (
        total +
        queue.reduce((sum, entry) => {
          const count = entry?.meta?.count;
          return typeof count === "number" && count > 0
            ? sum + count
            : sum;
        }, 0)
      );
    } catch {
      return total;
    }
  }, 0);
};

export const useOfflineAttendeeCount = () => {
  const [count, setCount] = useState(getTotalAttendeeCount());

  useEffect(() => {
    const update = () => setCount(getTotalAttendeeCount());

    window.addEventListener("storage", update);
    window.addEventListener(OFFLINE_QUEUE_EVENT, update);

    return () => {
      window.removeEventListener("storage", update);
      window.removeEventListener(OFFLINE_QUEUE_EVENT, update);
    };
  }, []);

  return count;
};

/* =====================================================
   GLOBAL SYNC (STORAGE-DRIVEN, NOT REGISTRY)
   ===================================================== */

export const syncAllOfflineQueues = async (options ={}) => {
  if (isGlobalSyncInProgress) {
    return;
  }

  isGlobalSyncInProgress = true;
  let syncResult;

  try {
    const entries = Array.from(offlineQueueRegistry.values());
    const results = [];
    const processedKeys = new Set();

    // CASE 1: hooks exist → use them
    if (entries.length) {
      for (const entry of entries) {
        processedKeys.add(entry.storageKey);

        try {
          const value = await entry.syncNow();
          results.push({
            storageKey: entry.storageKey,
            result: { status: "fulfilled", value },
          });
        } catch (error) {
          results.push({
            storageKey: entry.storageKey,
            result: { status: "rejected", reason: error },
          });
        }
      }
    }

    // Fallback (single unified pass — no duplication)
    const keys = getOfflineQueueKeys();
    for (const key of keys) {
      if (processedKeys.has(key)) continue;

      const { mutation } = getQueueConfig(key);
      if (!mutation) continue;

      const fallback = await flushQueueEngine({
        storageKey: key,
        executeMutation: (payload) => mutation(payload),
        ...options
      });

      if (fallback) {
        results.push({
          storageKey: key,
          result: { status: "fulfilled", value: fallback },
        });
      }
    }

    const summaries = results
      .filter((item) => item.result.status === "fulfilled" && item.result.value)
      .map((item) => item.result.value);

    syncResult = {
      total: summaries.reduce((acc, summary) => acc + Number(summary.total || 0), 0),
      syncedCount: summaries.reduce((acc, summary) => acc + Number(summary.syncedCount || 0), 0),
      failedCount: summaries.reduce((acc, summary) => acc + Number(summary.failedCount || 0), 0),
      remainingCount: summaries.reduce((acc, summary) => acc + Number(summary.remainingCount || 0), 0),
      status: "completed",
      results,
    };

    return syncResult;
  } finally {
    isGlobalSyncInProgress = false;

    // dedicated signal (clean separation)
    window.dispatchEvent(
      new CustomEvent("offline-queue-sync-complete", {
        detail: syncResult ?? {
          total: 0,
          syncedCount: 0,
          failedCount: 0,
          remainingCount: 0,
          results: [],
          status: "error"
        }
      })
    );
  }
};

/* =====================================================
   GLOBAL FLUSH
   ===================================================== */
const flushQueueEngine = async ({
  storageKey,
  executeMutation,
  onAuthError,
  onFlush = (summary) => {
    console.log("[OfflineQueue] Synced:", summary);
  },
  serialize = JSON.stringify,
  deserialize = JSON.parse,
}) => {
  const queue = readQueue(storageKey, deserialize);

  if (!Array.isArray(queue) || !queue.length) {
    return {
      total: 0,
      syncedCount: 0,
      failedCount: 0,
      remainingCount: 0,
      synced: [],
      failedAttempts: [],
      hasAuthError: false,
      status: "success",
    };
  }

  const remaining = [];
  const synced = [];
  const failedAttempts = [];
  let hasAuthError = false;

  for (const item of queue) {
    // prevents unnecessary API calls
    const attempts = Number(item?.meta?.attempts || 0) + 1;
    const baseDelay = 1000; // 1s
    const delay = Math.min(baseDelay * 2 ** (attempts - 1), 30000);
    // optional: skip retry if too soon
    const lastAttempt = item?.meta?.lastAttemptAt ? new Date(item.meta.lastAttemptAt).getTime() : 0;
    if (Date.now() - lastAttempt < delay) {
      remaining.push(item);
      continue;
    }

    try {
      const response = await executeMutation(item);
      synced.push({ item, response });

    } catch (error) {
      const is401 = error?.response?.status === 401;

      if (is401) {
        hasAuthError = true;

        remaining.push({
          ...item,
          meta: {
            ...(item.meta || {}),
            isAuthError: true,
            lastError: "Unauthorized (401)",
            lastAttemptAt: new Date().toISOString(),
          },
        });

      } else {
        const reason =
          error?.response?.data?.message ||
          error?.message ||
          "Sync failed";

        const failedItem = {
          ...item,
          meta: {
            ...(item.meta || {}),
            attempts,
            lastError: reason,
            lastAttemptAt: new Date().toISOString(),
          },
        };

        failedAttempts.push({
          id: failedItem.id,
          reason,
          attempts,
        });

        remaining.push(failedItem);
      }
    }
  }

  writeQueue(storageKey, remaining, serialize);

  let status;
  if (hasAuthError) status = "auth-error";
  else if (failedAttempts.length === 0) status = "success";
  else if (synced.length === 0) status = "failed";
  else status = "partial";

  const summary = {
    timestamp: new Date().toISOString(),
    storageKey,
    total: queue.length,
    syncedCount: synced.length,
    failedCount: remaining.length,
    remainingCount: remaining.length,
    synced,
    failedAttempts,
    hasAuthError,
    status,
  };

  persistSyncHistory(storageKey, summary);
  onFlush?.(summary);
  if (summary?.hasAuthError) {
    onAuthError?.({ type: "TOKEN_EXPIRED", summary });
  }

  return summary;
};


export const useOfflineQueueControl = () => {
  const [registryState, setRegistryState] = useState(getOfflineQueueRegistryState);

  useEffect(() => {
    const onRegistryUpdate = (event) => {
      if (event?.detail) {
        setRegistryState(event.detail);
        return;
      }
      setRegistryState(getOfflineQueueRegistryState());
    };

    window.addEventListener(OFFLINE_QUEUE_REGISTRY_EVENT, onRegistryUpdate);
    setRegistryState(getOfflineQueueRegistryState());

    return () => {
      window.removeEventListener(OFFLINE_QUEUE_REGISTRY_EVENT, onRegistryUpdate);
    };
  }, []);

  return {
    ...registryState,
    syncAllOfflineQueues,
  };
};

const executeQueueMutation = async ({
  mutation,
  payload,
  mockMutation,
  useMockMutation = false,
}) => {
  if (useMockMutation && typeof mockMutation === "function") {
    return await mockMutation(payload);
  }

  return await mutation(payload);
};

export const useOfflineMutationQueue = ({
  storageKey,
  mockMutation,
  useMockMutation = false,
  onFlush = (summary) => {
      console.log("[OfflineQueue] Synced:", summary);
  },
  onOnline,
  onOffline,
  onAuthError,
  serialize = defaultSerialize,
  deserialize = defaultDeserialize,
}) => {
  const isFlushingRef = useRef(false);
  const { mutation } = getQueueConfig(storageKey);

  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState(null);
  const [isOnline, setIsOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [hasQueuedItems, setHasQueuedItems] = useState(false);

  if (!mutation && !(useMockMutation && mockMutation)) {
    console.error(
      `[OfflineQueue] No mutation configured for storageKey: ${storageKey}`
    );
  }

  const executeMutation = useCallback(
    async (payload) =>
      executeQueueMutation({
        mutation,
        payload,
        mockMutation,
        useMockMutation,
      }),
    [mutation, mockMutation, useMockMutation]
  );

  const getQueueCount = useCallback(
    () => readQueue(storageKey, deserialize).length,
    [storageKey, deserialize]
  );
  const getQueueSizeBytes = useCallback(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return estimateBytes(raw);
    } catch {
      return 0;
    }
  }, [storageKey]);

  const [queueCount, setQueueCount] = useState(getQueueCount);
  const [queueSizeBytes, setQueueSizeBytes] = useState(getQueueSizeBytes);

  const enqueue = useCallback(
    (transaction) => {
      const queue = readQueue(storageKey, deserialize);
      queue.push({ ...transaction, isOnline: false });
      writeQueue(storageKey, queue, serialize);
      setQueueCount(queue.length);
      setQueueSizeBytes(getQueueSizeBytes());
      setHasQueuedItems(true);
      console.log(`[OfflineQueue] Item enqueued. Queue size: ${queue.length}`, storageKey);
      return queue.length;
    },
    [storageKey, deserialize, serialize, getQueueSizeBytes],
  );

  const flushQueue = useCallback(async () => {
    if (!navigator.onLine || isFlushingRef.current) {
      return {
        syncedCount: 0,
        failedCount: 0,
        remainingCount: getQueueCount(),
      };
    }

    const queue = readQueue(storageKey, deserialize);
    if (!queue.length) {
      setHasQueuedItems(false);
      return { syncedCount: 0, failedCount: 0, remainingCount: 0 };
    }

    isFlushingRef.current = true;
    setIsSyncing(true);

    const summary = await flushQueueEngine({
      storageKey,
      executeMutation,
      onAuthError,
      onFlush,
      serialize,
      deserialize,
    });

    // state updates (restored)
    setQueueCount(summary.remainingCount);
    setQueueSizeBytes(getQueueSizeBytes());
    setHasQueuedItems(summary.remainingCount > 0);

    // cleanup
    isFlushingRef.current = false;
    setIsSyncing(false);

    console.warn(`Summary Offline data(${JSON.stringify(summary)})`);

    setLastSyncResult(summary);

    return summary;
  }, [storageKey, deserialize, executeMutation, onAuthError, serialize, getQueueSizeBytes, onFlush, getQueueCount]);

  const runWithOfflineFallback = useCallback(
    async (payload, meta = {}) => {

      const transaction = {
        ...payload,
        id: crypto.randomUUID(),
        meta: {...meta, userId: getUserId(), },
        isOnline: true,
        createdAt: new Date().toISOString(),
      };

      if (!navigator.onLine) {
        enqueue(transaction);
        return { queued: true };
      }

      try {
        const response = await executeMutation(transaction);
        return { queued: false, response };
      } catch (error) {
        if (!error?.response) {
          enqueue(transaction);
          return { queued: true, error };
        }

        throw error;
      }
    },
    [enqueue, executeMutation],
  );

  const syncNow = flushQueue;

  useEffect(() => {
    setQueueCount(getQueueCount());
    setQueueSizeBytes(getQueueSizeBytes());
    setHasQueuedItems(getQueueCount() > 0);

    const handleOnline = () => {
      console.log("[OfflineQueue] App is now online, starting sync...", storageKey);
      setIsOnline(true);
      onOnline?.();
      flushQueue();
    };

    const handleOffline = () => {
      console.log("[OfflineQueue] App is now offline", storageKey);
      setIsOnline(false);
      onOffline?.();
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Initial sync if online
    if (navigator.onLine) {
      flushQueue();
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [flushQueue, getQueueCount, getQueueSizeBytes, onOnline, onOffline, storageKey]);

  useEffect(() => {
    offlineQueueRegistry.set(storageKey, {
      storageKey,
      queueCount,
      queueSizeBytes,
      isSyncing,
      lastSyncResult,
      syncNow,
      attendeeCount: getTotalAttendeeCount()
    });
    publishRegistryState();

    return () => {
      offlineQueueRegistry.delete(storageKey);
      publishRegistryState();
    };
  }, [isSyncing, lastSyncResult, queueCount, queueSizeBytes, storageKey, syncNow]);

  return useMemo(
    () => ({
      queueCount,
      queueSizeBytes,
      isSyncing,
      isOnline,
      hasQueuedItems,
      lastSyncResult,
      enqueue,
      flushQueue,
      syncNow,
      runWithOfflineFallback,
    }),
    [enqueue, flushQueue, isSyncing, isOnline, lastSyncResult, queueCount, queueSizeBytes, hasQueuedItems, runWithOfflineFallback, syncNow],
  );
};

export default useOfflineMutationQueue;